
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { BoPanel, IOnOffHandlers } from 'bonomo';
import { ICommunity } from '@/model/Community/communityInterfaces';
import { COMMUNITY_VERTICALS } from '@/model/Community/communityConstants';
import { IModule } from '@/model/Community/communityInterfaces';
import { IErrors } from '@/model/IErrors';
import { IAuthUserData } from '@/store/modules/authModule';
import { EUserPermissions } from '@/model/EUserPermissions';
import { EPermissionActions } from '@/model/EPermissionActions';
import { PermissionHelper } from '@/helpers/PermissionHelper';
import { IShortChannel } from '@/model/shortInterfaces';
import CommunityNav from '@/components/community/widget/CommunityNav.vue';
import SimpleConfirmationDialog from '@/components/shared/SimpleConfirmationDialog.vue';

const communityModule = namespace('communityModule');
const authModule = namespace('authModule');
const channelModule = namespace('channelModule');
const clientModule = namespace('clientModule');

@Component({
  name: 'CommunityEdit',
  components: { BoPanel, CommunityNav, SimpleConfirmationDialog },
})
export default class CommunityEdit extends Vue {
  @Prop()
  protected communityId!: number;

  @communityModule.Getter('community')
  protected community!: ICommunity;

  @communityModule.Getter('errors')
  protected errors!: IErrors;

  @communityModule.Getter('countries')
  protected countries!: { id: number; name: string }[];

  @communityModule.Getter('availableModules')
  protected availableModules!: IModule[];

  @communityModule.Action('fetchCommunity')
  private fetchCommunity!: (id: number) => Promise<any>;

  @communityModule.Action('updateCommunity')
  private updateCommunity!: (payload: { communityId: number; data: any }) => Promise<any>;

  @communityModule.Action('fetchCountries')
  private fetchCountries!: () => Promise<any>;

  @communityModule.Action('fetchModules')
  private fetchModules!: () => Promise<any>;

  @communityModule.Action('deleteCommunity')
  private deleteCommunity!: (communityId: number) => Promise<any>;

  @communityModule.Action('restoreCommunity')
  private restoreCommunity!: (communityId: number) => Promise<any>;

  @channelModule.Getter('channels')
  private channels!: IShortChannel[];

  @channelModule.Action('fetchChannels')
  private fetchChannels!: () => void;

  @clientModule.Getter('allBusinessClients')
  protected allBusinessClients!: { id: number; name: string }[];

  @clientModule.Action('fetchAllBusinessClients')
  private fetchAllBusinessClients!: () => Promise<any>;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  protected loading: boolean = true;
  protected saving: boolean = false;
  protected loadingClients: boolean = false;
  protected menuStartDate: boolean = false;
  protected menuEndDate: boolean = false;
  protected deleteDialogVisible: boolean = false;
  protected restoreDialogVisible: boolean = false;
  private subscribes: IOnOffHandlers = {};
  private form: any = {};

  get isDeveloper(): boolean {
    return PermissionHelper.hasPermissionAny(this.authUser, EUserPermissions.PERMISSION_DEVELOPER);
  }

  get canManageSecurityCode(): boolean {
    return (this as any).$canAction(EPermissionActions.ACTION_SECURITY_CODES_GENERATE)
      || PermissionHelper.hasRoleAdministrator(this.authUser);
  }

  get canAdmin(): boolean {
    return (this as any).$canAction(EPermissionActions.ACTION_COMMUNITIES_ADMIN);
  }

  get isDeleted(): boolean {
    return !!(this.community && this.community.deleted_at);
  }

  get channelOptions(): IShortChannel[] {
    return this.channels || [];
  }

  get statusOptions() {
    return [
      { text: this.$t('communities.status.active'), value: 'active' },
      { text: this.$t('communities.status.inactive'), value: 'inactive' },
      { text: this.$t('communities.status.deleted'), value: 'deleted' },
    ];
  }

  get currencyOptions() {
    return [
      { text: 'EUR', value: 'EUR' },
    ];
  }

  get clientTypeOptions() {
    return [
      { text: this.$t('communities.fields.clientTypes.trial'), value: 'trial' },
      { text: this.$t('communities.fields.clientTypes.live'), value: 'live' },
    ];
  }

  get verticalOptions() {
    return COMMUNITY_VERTICALS.map((id) => ({
      text: this.$t('lockers.locker.fields.vertical.values.' + id),
      value: id,
    }));
  }

  get moduleOptions() {
    return (this.availableModules || []).map((m: IModule) => ({
      text: m.name,
      value: m.id,
    }));
  }

  get availableLanguages() {
    return ['es-ES', 'ca-ES', 'en-GB', 'fr-FR', 'pt-PT', 'dk-DK', 'de-DE'];
  }

  private rules = {
    required: (v: any) => !!v || this.$t('common.validation.required'),
    requiredArray: (v: any[]) => (v && v.length > 0) || this.$t('common.validation.required'),
    min2: (v: string) => (v && v.length >= 2) || this.$t('common.validation.min', { min: 2 }),
  };

  created() {
    this.subscribes = this.$ons({
      'backend.call.OK.communityLoaded': this.initForm,
      'backend.call.begin.communityUpdated': () => { this.saving = true; },
      'backend.call.OK.communityUpdated': this.onSaveSuccess,
      'backend.call.KO.communityUpdated': () => { this.saving = false; },
    });
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  loadData() {
    this.fetchCommunity(this.communityId);
    this.fetchCountries();
    this.fetchModules();
    this.loadingClients = true;
    this.fetchAllBusinessClients().finally(() => { this.loadingClients = false; });
    if (this.isDeveloper) {
      this.fetchChannels();
    }
  }

  private initForm() {
    if (!this.community || !this.community.id) return;
    this.form = {
      name: this.community.name,
      slug: this.community.slug,
      channel_id: this.community.channel_id,
      status: this.community.status,
      languages: this.community.languages ? [...this.community.languages] : [],
      currency: this.community.currency || 'EUR',
      location_name: this.community.location_name,
      location_line_1: this.community.location_line_1,
      location_line_2: this.community.location_line_2,
      location_city: this.community.location_city,
      location_postcode: this.community.location_postcode,
      location_country_id: this.community.location_country_id,
      coordinates_lat: this.community.coordinates_lat,
      coordinates_long: this.community.coordinates_long,
      native_app_enabled: this.community.native_app_enabled,
      rentback_enabled: this.community.rentback_enabled,
      rentback_cap: this.community.rentback_cap,
      monthly_rentback_credit: this.community.monthly_rentback_credit,
      monthly_rentback_cap: this.community.monthly_rentback_cap,
      b2b_communications_enabled: this.community.b2b_communications_enabled,
      client_type: this.community.client_type || 'trial',
      vertical: this.community.vertical != null ? this.community.vertical : null,
      use_security_code: this.community.use_security_code,
      module_ids: this.community.modules ? this.community.modules.map((m: IModule) => m.id) : [],
      business_client_id: this.community.business_client_id || null,
      contract_start_date: this.isoToDateInput(this.community.contract_start_date),
      contract_end_date: this.isoToDateInput(this.community.contract_end_date),
      maintenance_fee: this.community.maintenance_fee,
      saas_fee: this.community.saas_fee,
      hardware_fee: this.community.hardware_fee,
      hardware_sale_price: this.community.hardware_sale_price,
      units: this.community.units,
    };
  }

  toggleModule(moduleId: number) {
    const idx = this.form.module_ids.indexOf(moduleId);
    if (idx === -1) {
      this.form.module_ids = [...this.form.module_ids, moduleId];
    } else {
      this.form.module_ids = this.form.module_ids.filter((id: number) => id !== moduleId);
    }
  }

  formatDateDisplay(value: string): string {
    if (!value) return '';
    const [y, m, d] = value.split('-');
    return `${d}/${m}/${y}`;
  }

  private isoToDateInput(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private onSave() {
    const data = { ...this.form };
    if (!data.contract_start_date) data.contract_start_date = null;
    if (!data.contract_end_date) data.contract_end_date = null;
    this.updateCommunity({ communityId: this.communityId, data });
  }

  private onSaveSuccess() {
    this.saving = false;
    this.$uiBus.emit('success', this.$t('common.saved'));
    this.$router.push({ name: 'community', params: { communityId: '' + this.communityId } });
  }

  private onCancel() {
    this.$router.push({ name: 'community', params: { communityId: '' + this.communityId } });
  }

  onConfirmDelete() {
    this.deleteCommunity(this.communityId).then(() => {
      this.deleteDialogVisible = false;
      this.$uiBus.emit('success', this.$t('communities.edit.softDelete'));
      this.$router.push({ name: 'communityList' });
    });
  }

  onConfirmRestore() {
    this.restoreCommunity(this.communityId).then(() => {
      this.restoreDialogVisible = false;
      this.$uiBus.emit('success', this.$t('communities.edit.restore'));
      this.fetchCommunity(this.communityId);
    });
  }
}
